import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

export const pageStack: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: { xs: theme.spacing(1.75), lg: theme.spacing(2.5) },
});

export const desktopTopSection: SxProps<Theme> = {
  display: { xs: "none", lg: "flex" },
  flexDirection: "column",
  gap: 2.5,
};

export const backLink: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  fontSize: "0.8125rem",
  color: "text.secondary",
  textDecoration: "none",
  width: "fit-content",
  transition: "color 0.2s ease",
  "&:hover": {
    color: "primary.main",
  },
};

export const titleRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 2,
};

export const titleGroup: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.75,
  flexWrap: "wrap",
};

export const orderTitle: SxProps<Theme> = {
  fontSize: "1.5rem",
  fontWeight: 700,
  direction: "ltr",
  textAlign: "start",
};

export const createdAt: SxProps<Theme> = {
  fontSize: "0.8125rem",
  color: "text.secondary",
};

export const actionsRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
};

export const ghostButton: SxProps<Theme> = (theme) => ({
  appearance: "none",
  WebkitAppearance: "none",
  outline: "none",
  height: 42,
  px: 2.25,
  borderRadius: 1.5,
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: "background.paper",
  color: "text.primary",
  fontFamily: "inherit",
  fontWeight: 700,
  fontSize: "0.84375rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.875,
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: alpha(theme.palette.primary.main, 0.3),
    color: "primary.main",
  },
  "&:disabled": {
    opacity: 0.55,
    cursor: "not-allowed",
    color: "text.disabled",
    borderColor: theme.palette.divider,
  },
});

export const copyLinkButton: SxProps<Theme> = (theme) => ({
  appearance: "none",
  WebkitAppearance: "none",
  outline: "none",
  width: "100%",
  height: 42,
  px: 2.25,
  borderRadius: 1.5,
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: "background.paper",
  color: "text.primary",
  fontFamily: "inherit",
  fontWeight: 700,
  fontSize: "0.84375rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.875,
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: alpha(theme.palette.primary.main, 0.3),
    color: "primary.main",
  },
  "&:disabled": {
    opacity: 0.55,
    cursor: "not-allowed",
    color: "text.disabled",
    borderColor: theme.palette.divider,
  },
});

export const statusButton: SxProps<Theme> = {
  height: 44,
  minWidth: 0,
  px: 2.75,
  fontWeight: 700,
  fontSize: "0.875rem",
  borderRadius: 1.5,
};

export const desktopLayout: SxProps<Theme> = (theme) => ({
  display: { xs: "none", lg: "grid" },
  gridTemplateColumns: "1fr 360px",
  gap: theme.spacing(2.5),
  alignItems: "start",
});

export const card: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 2.75,
  boxShadow: theme.shadows[3],
  p: theme.spacing(2.75, 3),
  display: "flex",
  flexDirection: "column",
  gap: 1,
});

export const cardHeader: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 0.75,
};

export const cardTitle: SxProps<Theme> = {
  fontSize: "1rem",
  fontWeight: 700,
};

export const sidebarCardTitle: SxProps<Theme> = {
  fontSize: "0.9375rem",
  fontWeight: 700,
};

export const table: SxProps<Theme> = {
  width: "100%",
  borderCollapse: "collapse",
};

export const th: SxProps<Theme> = {
  fontSize: "0.78125rem",
  fontWeight: 600,
  color: "text.secondary",
  textAlign: "start",
  pb: 1.25,
  px: 0.5,
};

export const td: SxProps<Theme> = (theme) => ({
  fontSize: "0.84375rem",
  color: "text.primary",
  py: 1.75,
  px: 0.5,
  borderTop: `1px solid ${theme.palette.divider}`,
  verticalAlign: "middle",
});

export const productCell: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
};

export const timelineItem: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.75,
};

export const timelineRail: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  alignSelf: "stretch",
};

export const timelineDot =
  (bg: string, border: string): SxProps<Theme> =>
  () => ({
    width: 30,
    height: 30,
    borderRadius: "50%",
    bgcolor: bg,
    border: `2px solid ${border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  });

export const timelineLine =
  (color: string): SxProps<Theme> =>
  () => ({
    width: 2,
    flex: 1,
    minHeight: 22,
    bgcolor: color,
  });

export const timelineContent: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
  pb: 2.5,
};

export const timelineTitle =
  (color: string): SxProps<Theme> =>
  () => ({
    fontSize: "0.875rem",
    fontWeight: 600,
    color,
  });

export const timelineTime: SxProps<Theme> = {
  fontSize: "0.75rem",
  color: "text.secondary",
};

export const customerCard: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 2.75,
  boxShadow: theme.shadows[3],
  p: 2.75,
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

export const profileRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
};

export const avatar: SxProps<Theme> = (theme) => ({
  width: 46,
  height: 46,
  borderRadius: "50%",
  bgcolor: alpha(theme.palette.primary.main, 0.12),
  color: "primary.main",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: "1.0625rem",
  flexShrink: 0,
});

export const contactList: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  borderTop: `1px solid ${theme.palette.divider}`,
  pt: 1.75,
});

export const contactRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.25,
  fontSize: "0.8125rem",
  color: "text.secondary",
  lineHeight: 1.7,
};

export const paymentRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "0.8125rem",
};

export const paymentLabel: SxProps<Theme> = {
  color: "text.secondary",
};

export const paymentValue: SxProps<Theme> = {
  fontWeight: 500,
};

export const paymentTotalRow: SxProps<Theme> = (theme) => ({
  borderTop: `1px dashed ${theme.palette.divider}`,
  pt: 1.75,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

export const paymentTotalLabel: SxProps<Theme> = {
  fontSize: "0.875rem",
  fontWeight: 700,
};

export const paymentTotalValue: SxProps<Theme> = {
  fontSize: "1.125rem",
  fontWeight: 800,
};

export const paymentBanner: SxProps<Theme> = (theme) => ({
  bgcolor: alpha(theme.palette.warning.main, 0.1),
  borderRadius: 1.5,
  p: theme.spacing(1.5, 1.75),
  display: "flex",
  alignItems: "center",
  gap: 1.125,
});

export const paymentBannerText: SxProps<Theme> = {
  fontSize: "0.78125rem",
  fontWeight: 600,
  color: "warning.main",
};

export const sideActions: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
};

export const notFoundCard: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 2.75,
  boxShadow: theme.shadows[3],
  p: theme.spacing(8),
  display: { xs: "none", lg: "flex" },
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  textAlign: "center",
  minHeight: 480,
});

export const notFoundIconWrap: SxProps<Theme> = (theme) => ({
  width: 110,
  height: 110,
  borderRadius: 3.5,
  bgcolor: alpha(theme.palette.error.main, 0.08),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: 1,
});

export const mobileHeaderBar: SxProps<Theme> = (theme) => ({
  display: { xs: "flex", lg: "none" },
  alignItems: "center",
  gap: 1.5,
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 1.75,
  p: theme.spacing(1.75, 2.25),
  boxShadow: theme.shadows[1],
});

export const mobileHeaderText: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  lineHeight: 1.3,
  minWidth: 0,
};

export const mobileHeaderTitle: SxProps<Theme> = {
  fontSize: "0.9375rem",
  fontWeight: 700,
  direction: "ltr",
  textAlign: "start",
};

export const mobileHeaderDate: SxProps<Theme> = {
  fontSize: "0.71875rem",
  color: "text.secondary",
};

export const mobileIconButton: SxProps<Theme> = (theme) => ({
  width: 38,
  height: 38,
  borderRadius: 1.25,
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: "background.paper",
  color: "text.secondary",
  flexShrink: 0,
});

export const mobileStatusBanner: SxProps<Theme> = (theme) => ({
  display: { xs: "flex", lg: "none" },
  alignItems: "center",
  justifyContent: "space-between",
  bgcolor: alpha(theme.palette.warning.main, 0.1),
  borderRadius: 1.75,
  p: theme.spacing(1.5, 1.75),
});

export const mobileStack: SxProps<Theme> = (theme) => ({
  display: { xs: "flex", lg: "none" },
  flexDirection: "column",
  gap: theme.spacing(1.75),
});

export const mobileCard: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 2,
  boxShadow: theme.shadows[1],
  p: 1.75,
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
});

export const mobileCardTitle: SxProps<Theme> = {
  fontSize: "0.84375rem",
  fontWeight: 700,
};

export const mobileAvatar: SxProps<Theme> = (theme) => ({
  width: 42,
  height: 42,
  borderRadius: "50%",
  bgcolor: alpha(theme.palette.primary.main, 0.12),
  color: "primary.main",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: "1rem",
  flexShrink: 0,
});

export const mobileCustomerRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.375,
};

export const mobileAddressRow: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 1.125,
  fontSize: "0.78125rem",
  color: "text.secondary",
  lineHeight: 1.7,
  borderTop: `1px solid ${theme.palette.divider}`,
  pt: 1.375,
});

export const mobileItemRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.375,
};

export const mobilePaymentRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "0.78125rem",
};

export const mobilePaymentTotalRow: SxProps<Theme> = (theme) => ({
  borderTop: `1px dashed ${theme.palette.divider}`,
  pt: 1.25,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

export const mobilePaymentTotalLabel: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 700,
};

export const mobilePaymentTotalValue: SxProps<Theme> = {
  fontSize: "0.9375rem",
  fontWeight: 800,
};

export const mobileResendButton: SxProps<Theme> = (theme) => ({
  height: 48,
  borderRadius: 1.5,
  fontWeight: 700,
  fontSize: "0.875rem",
  boxShadow: `0 10px 20px ${alpha(theme.palette.primary.dark, 0.25)}`,
});

export const mobileNotFound: SxProps<Theme> = {
  display: { xs: "flex", lg: "none" },
  flex: 1,
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  textAlign: "center",
  py: 8.75,
  px: 1.5,
};

export const mobileNotFoundIconWrap: SxProps<Theme> = (theme) => ({
  width: 96,
  height: 96,
  borderRadius: 3.25,
  bgcolor: alpha(theme.palette.error.main, 0.08),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: 1,
});

export const mobileNotFoundTitle: SxProps<Theme> = {
  fontSize: "1.03125rem",
  fontWeight: 700,
};

export const mobileNotFoundDescription: SxProps<Theme> = {
  maxWidth: 250,
  lineHeight: 1.8,
  fontSize: "0.78125rem",
  color: "text.secondary",
};

export const mobileNotFoundButton: SxProps<Theme> = (theme) => ({
  mt: 1.75,
  height: 46,
  px: 2.75,
  borderRadius: 1.5,
  fontSize: "0.875rem",
  fontWeight: 700,
  boxShadow: `0 10px 20px ${alpha(theme.palette.primary.dark, 0.25)}`,
});

export const loadingWrap: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  py: 8,
};

export const statusMenuItem: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 500,
};
